/**
 * Shared transient-failure handling for calls to the n8n backend (via APIM).
 *
 * n8n serves `503 {"code":503,"message":"Database is not ready!"}` while its
 * Postgres connection is still coming up, and APIM/ingress returns 502/503/504
 * with no ready replica during an n8n cold start or a revision rollover. These
 * are self-healing: the same request a moment later succeeds. Retry them with
 * backoff so a deploy or a DB blip doesn't surface as a hard error, and use
 * TRANSIENT_MESSAGE instead of leaking the backend's raw wording to the user.
 */

export const TRANSIENT_STATUS = new Set([502, 503, 504]);

// User-facing copy for a transient failure — never exposes the backend's own
// message (e.g. "Database is not ready!").
export const TRANSIENT_MESSAGE =
  'The service is still starting up. Please try again in a moment.';

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => { clearTimeout(t); reject(new DOMException('Aborted', 'AbortError')); },
      { once: true },
    );
  });

export interface RetryOpts {
  attempts?: number;      // total tries (default 3)
  baseDelayMs?: number;   // first backoff, doubles each retry (default 800ms → 800/1600)
  signal?: AbortSignal;   // abort bails out immediately, no further retries
  onRetry?: (info: { attempt: number; status?: number; delayMs: number }) => void;
}

// Retries only transient 5xx (and network-level failures where no response was
// received). `makeRequest` MUST build a FRESH fetch on each call — request bodies
// (FormData, streams) are single-use. A 2xx/streaming response is returned
// untouched (its body is never read here), so this is safe to wrap around a
// streaming call.
export const fetchWithRetry = async (
  makeRequest: () => Promise<Response>,
  opts: RetryOpts = {},
): Promise<Response> => {
  const attempts = Math.max(1, opts.attempts ?? 3);
  const base = opts.baseDelayMs ?? 800;
  let lastErr: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      const res = await makeRequest();
      if (!TRANSIENT_STATUS.has(res.status) || attempt === attempts) return res;
      try { await res.body?.cancel(); } catch { /* free the socket before retrying */ }
      const delayMs = base * 2 ** (attempt - 1);
      opts.onRetry?.({ attempt, status: res.status, delayMs });
      await sleep(delayMs, opts.signal);
    } catch (err) {
      if ((err as any)?.name === 'AbortError') throw err; // user Stop / navigation
      lastErr = err;
      if (attempt === attempts) throw err;
      const delayMs = base * 2 ** (attempt - 1);
      opts.onRetry?.({ attempt, delayMs });
      await sleep(delayMs, opts.signal);
    }
  }
  throw lastErr;
};
